import { mergeWith } from 'lodash';

const defaultMergeArgs = <T>(a: T, b: T): T =>
    mergeWith(a, b, (a: unknown, b: unknown) => {
        // If they're both arrays then do a concat
        if (Array.isArray(a) && Array.isArray(b)) return a.concat(b);

        // Otherwise, leave it to the default merge behavior
        return undefined;
    });

/**
 * For an action creator that accepts arguments with arrays and uses a redux thunk pattern,
 * this will create a debounced version that will combine the multiple calls into a single call
 * and use a combined set of arguments.
 */
export const createDebouncedActionDispatcher = <R, Params extends unknown[] = unknown[]>(
    debounceActionCreator: Function,
    {
        debounceTime = 250,
        mergeArgs = defaultMergeArgs,
    }: { debounceTime?: number; mergeArgs?: (a: Readonly<Params>, b: Readonly<Params>) => Params } = {},
) => {
    let fetchTimeout: ReturnType<typeof setTimeout>;
    let pendingArgs = [] as unknown as Params;
    let pendingPromises: { resolve: (value: R) => void; reject: (reason?: unknown) => void }[] = [];

    return (...args: Params) =>
        async (dispatch: Function): Promise<R> =>
            new Promise((resolve, reject) => {
                pendingArgs = mergeArgs(pendingArgs, args);
                pendingPromises.push({ resolve, reject });

                clearTimeout(fetchTimeout);

                fetchTimeout = setTimeout(() => {
                    const argsToProcess = pendingArgs;
                    const promisesToResolve = pendingPromises;

                    pendingArgs = [] as unknown as Params;
                    pendingPromises = [];

                    try {
                        const result = dispatch(debounceActionCreator(...argsToProcess));
                        promisesToResolve.forEach(({ resolve }) => resolve(result));
                    } catch (error) {
                        promisesToResolve.forEach(({ reject }) => reject(error));
                    }
                }, debounceTime);
            });
};
